import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Actions, createEffect,  ofType} from '@ngrx/effects';
import { Store} from '@ngrx/store';
import {concat, empty, of} from 'rxjs';
import {tap, withLatestFrom} from 'rxjs/internal/operators';
import {catchError, delay, map, switchMap} from 'rxjs/operators';

import {debug} from '../../../rxjs-operators';
import {LoadMyClientsAction} from '../actions/my-clients';
import {go} from '../actions/router';
import {error, success} from '../actions/toasts';
import {
  ClearImpersonationAction, LoadRolesAction,
  StartImpersonationAction,
  StartImpersonationServerErrorAction,
  StartImpersonationSuccessAction,
  StopImpersonationAction,
  userContextActionTypes
} from '../actions/user-context';
import {IMPERSONATION_DURATION, IMPERSONATION_TOKEN} from '../consts';
import {ImpersonateUser} from '../models/impersonate-user';
import * as fromShared from '../reducers';
import {UserContextService} from '../services/user-context.service';

import {BaseEffect} from './base.effect';
import {L10nTranslationService} from 'angular-l10n';

@Injectable()
export class UserContextImpersonationEffect extends BaseEffect {

  start$ = createEffect(() => { return this.actions$.pipe(ofType(userContextActionTypes.START_IMPERSONATION),
             debug('Start impersonation action received'),
             map((action: StartImpersonationAction) => action.payload),
             withLatestFrom(this.store.select(fromShared.selectors.getCurrentClient)))
             .pipe(switchMap(([payload, client]) => this.userContextService.createImpersonationToken(client.uuid, payload, IMPERSONATION_DURATION)
                                                      .pipe(
                                                        map((result: ImpersonateUser) => new StartImpersonationSuccessAction(result)),
                                                        catchError(res => this.catchResponseError(res)),
                                                        )))
  });


  startSuccess$ = createEffect(() => { return  this.actions$.pipe(ofType(userContextActionTypes.START_IMPERSONATION_SUCCESS),
      debug('Start impersonation success action received'),
            map((action: StartImpersonationSuccessAction) => action.payload),
            tap(payload => sessionStorage.setItem(IMPERSONATION_TOKEN, payload.token)),
            withLatestFrom(this.store.select(fromShared.selectors.getCurrentClient)))
      .pipe(switchMap(([payload, client]) => concat(of(
                                                       new LoadMyClientsAction(),
                                                       new LoadRolesAction(client.uuid),
                                                       go(['/', 'clients', 'list']),
                                                       success(this.translation.translate('USER_CONTEXT_IMPERSONATE_SUCCESS',
                                                                                          { username: payload.name, duration: IMPERSONATION_DURATION / 60 }),
                                                               this.translation.translate('TOAST_SUCCESS_TITLE'))),
                                                    of(new StopImpersonationAction()).pipe(delay(IMPERSONATION_DURATION * 1000)))))
  });


  startFail$ = createEffect(() => { return this.actions$.pipe(ofType(userContextActionTypes.START_IMPERSONATION_FAIL),
                   debug('A server error occurred while impersonating another user.'),
                       map(() => error(this.translation.translate('USER_CONTEXT_IMPERSONATE_ERROR'), this.translation.translate('TOAST_ERROR_TITLE'))))
  });


  stop$ = createEffect(() => { return this.actions$
            .pipe(ofType(userContextActionTypes.STOP_IMPERSONATION),
                  debug('Stop impersonation action received'),
                  withLatestFrom(this.store.select(fromShared.selectors.getImpersonateUser)),
                  switchMap(([action, impersonateUser]) => (impersonateUser == undefined ? empty() : of(new ClearImpersonationAction()))))
  });


  clear$ = createEffect(() => { return this.actions$.pipe(ofType(userContextActionTypes.CLEAR_IMPERSONATION),
                   debug('Clear impersonation action received'),
                   tap(() => sessionStorage.removeItem(IMPERSONATION_TOKEN)),
                   withLatestFrom(this.store.select(fromShared.selectors.getCurrentClient)),
                   switchMap(([action, client]) => of(new LoadMyClientsAction(), new LoadRolesAction(client.uuid), go(['/', 'clients', 'list']))))
  });

  constructor(private actions$: Actions,
              private store: Store<fromShared.AppState>,
              private translation: L10nTranslationService,
              private userContextService: UserContextService,
              router: Router) {
    super(router);
  }

  protected handleUnhandledError = (response) => new StartImpersonationServerErrorAction(response['error'].message)

  protected handleGoneError = (response) => {
    return new StartImpersonationServerErrorAction(response['error'].message);
  }
}
